import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { PartnerDealCommitteeRoundResponse } from '@interfaces/partners/responses/partner-deal-committee.response';
import { TranslateService } from '@ngx-translate/core';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { describeVoteConfirmation } from '@app/features/dashboard/commercial/pages/deal-detail/components/deal-committee-panel/deal-committee-presenter';

/** Voto final confirmado na modal. */
export type CommitteeFinalVote = 'Approve' | 'Deny';

/**
 * Confirmação de Aprovar ou Negar no comitê de crédito (card 3789).
 *
 * Os dois votos são finais e um deles pode decidir o deal, então a modal diz o **efeito** do voto
 * antes do clique ("Com o seu voto o deal é aprovado e vai para Cotação", "A maioria se forma, mas o
 * comitê só decide depois que a contestação em aberto for respondida"). Mostra também o texto que
 * vai junto, porque ele fica na discussão imutável e, no Negar, aparece para o parceiro.
 *
 * A modal só confirma: quem chama o servidor é o painel, que recebe o `confirmed`.
 */
@Component({
  selector: 'app-modal-committee-vote',
  standalone: true,
  templateUrl: './modal-committee-vote.component.html',
  styleUrls: ['./modal-committee-vote.component.scss'],
  imports: [CommonModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCommitteeVoteComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** A pessoa confirmou o voto. */
  @Output() confirmed = new EventEmitter<CommitteeFinalVote>();

  private readonly cdr = inject(ChangeDetectorRef);

  vote: CommitteeFinalVote = 'Approve';
  text = '';
  heading = '';
  description = '';
  confirmLabel = '';

  constructor(translateService: TranslateService) {
    super(translateService);
  }

  get isDeny(): boolean {
    return this.vote === 'Deny';
  }

  /** Abre a confirmação do voto, com o efeito calculado sobre a rodada atual. */
  open(round: PartnerDealCommitteeRoundResponse, vote: CommitteeFinalVote, text: string): void {
    const confirmation = describeVoteConfirmation(round, vote);
    this.vote = vote;
    this.text = (text ?? '').trim();
    this.heading = confirmation.title;
    this.description = confirmation.text;
    this.confirmLabel = confirmation.confirmLabel;
    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Voltar', '', 'committee-vote', false, true);
  }

  close(): void {
    this.dialogRef?.close();
  }

  submitAction(): void {
    this.confirmed.emit(this.vote);
    this.close();
  }
}
